import React, { useState } from 'react';
import {
  Typography,
  Card,
  Space,
  Button,
  Tag,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Checkbox,
  message,
  Modal,
  Spin,
  Alert,
  Switch,
  Row,
  Col,
} from 'antd';
import {
  ArrowLeftOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  StopOutlined,
  AppstoreOutlined,
  ApiOutlined,
  GoogleOutlined,
  GithubOutlined,
  WindowsOutlined,
  SlackOutlined,
  SafetyOutlined,
  LinkOutlined,
  DisconnectOutlined,
  FileTextOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, functions, db } from '../../../shared/lib/supabase';
import { 
  useWorkflowCredentials, 
  useCreateOAuthCredential, 
  useDeleteOAuthCredential 
} from '../../client/hooks/useOAuthCredentials';

const { Title, Paragraph, Text } = Typography;

interface ClientProduct {
  id: string;
  product_id: string;
  client_id: string;
  cloned_workflow_id: string | null;
  custom_workflow_id?: string | null;
  is_active: boolean;
  config: any;
  created_at: string;
  product: {
    id: string;
    name: string;
    description: string | null;
    workflow_id: string;
    n8n_instance_id: string;
    monthly_price: number | null;
    setup_fee: number | null;
    form_fields?: any[];
  };
  client: {
    id: string;
    full_name: string | null;
  };
}

// OAuth Credentials Section Component (Admin Version)
interface OAuthCredentialsSectionProps {
  clientProduct: ClientProduct;
  onConnect: (credential: any) => void;
}

const OAuthCredentialsSection: React.FC<OAuthCredentialsSectionProps> = ({ 
  clientProduct, 
  onConnect 
}) => {
  const { data: credentialsData, isLoading, error } = useWorkflowCredentials(clientProduct.id);
  const deleteCredential = useDeleteOAuthCredential();

  const handleDisconnect = async (credentialType: string) => {
    Modal.confirm({
      title: 'Desconectar API',
      content: 'Tem certeza que deseja desconectar esta API do cliente? O cliente precisará reconectar para usar este produto.',
      okText: 'Sim, desconectar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          message.loading({ content: 'Desconectando...', key: 'disconnect' });

          await deleteCredential.mutateAsync({
            client_product_id: clientProduct.id,
            credential_type: credentialType,
          });

          message.success({
            content: 'API desconectada com sucesso!',
            key: 'disconnect',
          });
        } catch (error: any) {
          message.error({
            content: error.message || 'Erro ao desconectar API',
            key: 'disconnect',
          });
        }
      },
    });
  };

  if (isLoading) {
    return (
      <Card loading={true}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="small" />
          <span style={{ marginLeft: 8 }}>Verificando conexões OAuth...</span>
        </div>
      </Card>
    );
  }

  if (error) {
    console.error('❌ Error loading credentials:', error);
    return (
      <Alert
        message="Erro ao carregar credenciais"
        description="Não foi possível verificar as credenciais OAuth deste produto."
        type="error"
        showIcon
      />
    );
  }

  // Para produtos customizados, o workflow_id do produto é suficiente
  // Não é necessário ter apenas cloned_workflow_id
  if ((!clientProduct.cloned_workflow_id && !clientProduct.custom_workflow_id) && !credentialsData?.credentials?.length) {
    return (
      <Alert
        message="Nenhuma API OAuth necessária"
        description="Este produto não requer conexões OAuth."
        type="info"
        showIcon
      />
    );
  }
  
  // Se não há cloned_workflow_id nem custom_workflow_id, não exibir seção
  if (!clientProduct.cloned_workflow_id && !clientProduct.custom_workflow_id) {
    return null;
  }

  if (!credentialsData || credentialsData.credentials.length === 0) {
    return (
      <Alert
        message="Nenhuma API OAuth necessária"
        description="Este produto não requer conexões OAuth."
        type="info"
        showIcon
      />
    );
  }

  const getCredentialIcon = (type: string) => {
    const icons: Record<string, any> = {
      googleCalendarOAuth2Api: <GoogleOutlined style={{ color: '#4285F4', fontSize: 20 }} />,
      googleDriveOAuth2Api: <GoogleOutlined style={{ color: '#4285F4', fontSize: 20 }} />,
      googleSheetsOAuth2Api: <GoogleOutlined style={{ color: '#4285F4', fontSize: 20 }} />,
      gmailOAuth2: <GoogleOutlined style={{ color: '#4285F4', fontSize: 20 }} />,
      githubOAuth2Api: <GithubOutlined style={{ fontSize: 20 }} />,
      microsoftOAuth2Api: <WindowsOutlined style={{ color: '#00A4EF', fontSize: 20 }} />,
      slackOAuth2Api: <SlackOutlined style={{ color: '#4A154B', fontSize: 20 }} />,
    };
    return icons[type] || <ApiOutlined style={{ fontSize: 20 }} />;
  };

  const getCredentialName = (type: string) => {
    const names: Record<string, string> = {
      googleCalendarOAuth2Api: 'Google Calendar',
      googleDriveOAuth2Api: 'Google Drive',
      googleSheetsOAuth2Api: 'Google Sheets',
      gmailOAuth2: 'Gmail',
      githubOAuth2Api: 'GitHub',
      microsoftOAuth2Api: 'Microsoft',
      slackOAuth2Api: 'Slack',
    };
    return names[type] || type;
  };

  return (
    <Card
      title={
        <Space>
          <ApiOutlined />
          <span>Credenciais OAuth do Cliente</span>
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Alert
          message="Gestão de APIs"
          description="Como administrador, você pode conectar APIs OAuth em nome do cliente caso ele tenha dificuldades."
          type="info"
          showIcon
        />

        {credentialsData.credentials.map((credential: any) => {
          const isConnected = credential.isConfigured;
          const credentialName = getCredentialName(credential.type);

          return (
            <Card
              key={credential.type}
              size="small"
              style={{
                borderLeft: `4px solid ${isConnected ? '#52c41a' : '#faad14'}`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Space size="middle">
                  {getCredentialIcon(credential.type)}
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>
                      {credentialName}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {credential.type}
                    </Text>
                  </div>
                </Space>

                <Space>
                  {isConnected ? (
                    <>
                      <Tag color="success" icon={<SafetyOutlined />}>
                        Conectado
                      </Tag>
                      <Button
                        size="small"
                        danger
                        icon={<DisconnectOutlined />}
                        onClick={() => handleDisconnect(credential.type)}
                      >
                        Desconectar
                      </Button>
                    </>
                  ) : (
                    <>
                      <Tag color="warning">Não conectado</Tag>
                      <Button
                        size="small"
                        type="primary"
                        icon={<LinkOutlined />}
                        onClick={() => onConnect(credential)}
                      >
                        Conectar para o Cliente
                      </Button>
                    </>
                  )}
                </Space>
              </div>

              {credential.fields && credential.fields.length > 0 && (
                <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
                  <Text type="secondary">
                    Campos necessários: {credential.fields.map((f: any) => f.displayName).join(', ')}
                  </Text>
                </div>
              )}
            </Card>
          );
        })}
      </Space>
    </Card>
  );
};

export const ClientProductDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { clientId, productId } = useParams<{ clientId: string; productId: string }>();
  const queryClient = useQueryClient();

  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isAdminConfigModalOpen, setIsAdminConfigModalOpen] = useState(false);
  const [isOAuthModalOpen, setIsOAuthModalOpen] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState<any>(null);
  const [productConfig, setProductConfig] = useState<any>(null);
  const [adminProductConfig, setAdminProductConfig] = useState<any>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [loadingAdminConfig, setLoadingAdminConfig] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [isSavingAdminConfig, setIsSavingAdminConfig] = useState(false);

  const [configForm] = Form.useForm();
  const [adminConfigForm] = Form.useForm();
  const [oauthForm] = Form.useForm();

  // Estado para visualização de formulários
  const [isFormSubmissionModalOpen, setIsFormSubmissionModalOpen] = useState(false);
  const [formSubmissionData, setFormSubmissionData] = useState<any>(null);

  // Fetch product details
  const { data: clientProduct, isLoading: loadingProduct } = useQuery({
    queryKey: ['admin-client-product', productId, clientId],
    queryFn: async () => {
      console.log('🔍 Fetching client product with:', { productId, clientId });
      
      if (!productId) {
        console.error('❌ No productId provided');
        return null;
      }

      // First, get the client_product with product info
      const { data: clientProductData, error: cpError } = await supabase
        .from('client_products')
        .select('*, product:products(*)')
        .eq('id', productId)
        .eq('client_id', clientId)
        .single();

      if (cpError) {
        console.error('❌ Error fetching client product:', cpError);
        console.error('Query params:', { productId, clientId });
        throw cpError;
      }

      // Then, get the user profile separately with fallback
      // Use the safe method that avoids 406 errors
      const { data: userProfileData } = clientId 
        ? await db.userProfiles.getByIdSafe(clientId)
        : { data: null };
      const userProfile = userProfileData;

      if (userProfile) {
        console.log('👤 User profile loaded:', userProfile);
      } else {
        console.warn('⚠️ User profile not found for clientId:', clientId);
      }

      const result = {
        ...clientProductData,
        client: userProfile || {
          id: clientId,
          full_name: null,
        },
      };
      
      console.log('✅ Client product loaded:', result);
      return result as ClientProduct;
    },
    enabled: !!productId && !!clientId,
  });

  // Fetch form submission for this client_product
  const { data: formSubmission, isLoading: loadingFormSubmission } = useQuery({
    queryKey: ['form-submission', productId],
    queryFn: async () => {
      if (!productId) return null;
      
      const { data, error } = await supabase
        .from('form_submissions')
        .select('*, form:forms(*)')
        .eq('client_product_id', productId)
        .order('submitted_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching form submission:', error);
        return null;
      }
      
      return data;
    },
    enabled: !!productId,
  });

  // Toggle product status mutation
  const toggleProductMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const result = await functions.toggleWorkflow(id, isActive);
      if (!result.success) {
        throw new Error(result.error || 'Falha ao alterar status do workflow');
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-client-product', productId] });
      message.success('Status do produto atualizado com sucesso!');
    },
    onError: (error: any) => {
      message.error(error.message || 'Erro ao atualizar status do produto');
    },
  });

  const createOAuthCredential = useCreateOAuthCredential();

  const handleToggleActive = async (isActive: boolean) => {
    if (!clientProduct) return;
    try {
      await toggleProductMutation.mutateAsync({ id: clientProduct.id, isActive });
    } catch (error) {
      // Error already handled by mutation
    }
  };

  const handleOpenConfigModal = async () => {
    if (!clientProduct) return;
    setIsConfigModalOpen(true);
    setLoadingConfig(true);

    try {
      const response = await functions.getWorkflowConfig(clientProduct.id);
      setProductConfig(response);

      if (response.current_values) {
        configForm.setFieldsValue(response.current_values);
      }
    } catch (error) {
      console.error('Erro ao buscar configurações:', error);
      message.error('Erro ao carregar configurações do produto');
      setProductConfig({ fields: [] });
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleOpenAdminConfigModal = async () => {
    if (!clientProduct) return;
    setIsAdminConfigModalOpen(true);
    setLoadingAdminConfig(true);

    try {
      const response = await functions.getProductConfig(clientProduct.id);
      setAdminProductConfig(response);

      if (response.current_values) {
        adminConfigForm.setFieldsValue(response.current_values);
      }
    } catch (error: any) {
      console.error('Erro ao buscar configurações admin:', error);
      const errorMessage = error?.message || error?.error || 'Erro ao carregar configurações do template';
      message.error({
        content: errorMessage,
        duration: 6,
      });
      setAdminProductConfig({ fields: [] });
      // Fechar modal em caso de erro
      setIsAdminConfigModalOpen(false);
    } finally {
      setLoadingAdminConfig(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!clientProduct) return;

    setIsSavingConfig(true);
    try {
      const values = await configForm.validateFields();
      message.loading({ content: 'Salvando configuração...', key: 'saveConfig' });

      await functions.updateWorkflowConfig(clientProduct.id, values);

      message.success({
        content: 'Configuração salva com sucesso!',
        key: 'saveConfig',
      });

      setIsConfigModalOpen(false);
      configForm.resetFields();
    } catch (error: any) {
      console.error('Error saving config:', error);
      if (error.errorFields) {
        message.error({
          content: 'Por favor, preencha todos os campos obrigatórios',
          key: 'saveConfig',
        });
      } else {
        message.error({
          content: error.message || 'Erro ao salvar configuração',
          key: 'saveConfig',
        });
      }
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleSaveAdminConfig = async () => {
    if (!clientProduct) return;

    setIsSavingAdminConfig(true);
    try {
      const values = await adminConfigForm.validateFields();
      message.loading({ content: 'Salvando configuração...', key: 'saveAdminConfig' });

      await functions.updateProductConfig(clientProduct.id, values);

      message.success({
        content: 'Configuração do template salva com sucesso!',
        key: 'saveAdminConfig',
      });

      setIsAdminConfigModalOpen(false);
      adminConfigForm.resetFields();
    } catch (error: any) {
      console.error('Error saving admin config:', error);
      if (error.errorFields) {
        message.error({
          content: 'Por favor, preencha todos os campos obrigatórios',
          key: 'saveAdminConfig',
        });
      } else {
        message.error({
          content: error.message || 'Erro ao salvar configuração',
          key: 'saveAdminConfig',
        });
      }
    } finally {
      setIsSavingAdminConfig(false);
    }
  };

  const handleOpenOAuthModal = (credential: any) => {
    setSelectedCredential(credential);
    setIsOAuthModalOpen(true);
    oauthForm.resetFields();
  };

  const handleCreateOAuthCredential = async () => {
    if (!clientProduct || !selectedCredential) return;

    try {
      const values = await oauthForm.validateFields();
      message.loading({ content: 'Criando credencial...', key: 'createOAuth' });

      const result = await createOAuthCredential.mutateAsync({
        client_product_id: clientProduct.id,
        credential_type: selectedCredential.type,
        credential_data: values,
      });

      message.success({
        content: result.message,
        key: 'createOAuth',
      });

      const currentCredential = selectedCredential;
      setIsOAuthModalOpen(false);
      setSelectedCredential(null);
      oauthForm.resetFields();

      // OAuth flow
      if (result.credential_id && currentCredential) {
        const handleOAuthMessage = async (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;

          if (event.data.type === 'OAUTH_SUCCESS' && event.data.code) {
            try {
              message.loading({ content: 'Finalizando autenticação...', key: 'finalizeOAuth' });

              const finalizeResult = await functions.finalizeOAuth({
                code: event.data.code,
                credential_id: result.credential_id,
                client_product_id: clientProduct.id,
              });

              if (finalizeResult.success) {
                message.success({
                  content: 'API conectada com sucesso!',
                  key: 'finalizeOAuth',
                  duration: 5,
                });
                queryClient.invalidateQueries({ 
                  queryKey: ['workflow-credentials', clientProduct.id] 
                });
              } else {
                throw new Error(finalizeResult.error || 'Falha ao finalizar OAuth');
              }
            } catch (error: any) {
              console.error('Error finalizing OAuth:', error);
              message.error({
                content: error.message || 'Erro ao finalizar autenticação',
                key: 'finalizeOAuth',
                duration: 5,
              });
            }
            window.removeEventListener('message', handleOAuthMessage);
          } else if (event.data.type === 'OAUTH_ERROR') {
            message.error({
              content: 'Erro na autenticação OAuth',
              key: 'finalizeOAuth',
            });
            window.removeEventListener('message', handleOAuthMessage);
          }
        };

        window.addEventListener('message', handleOAuthMessage);

        const redirectUri = `${window.location.origin}/oauth-callback.html`;
        const scopes = currentCredential.type === 'googleCalendarOAuth2Api'
          ? 'https://www.googleapis.com/auth/calendar'
          : 'https://www.googleapis.com/auth/calendar';

        const clientIdFromEnv = import.meta.env.VITE_GOOGLE_CLIENT_ID;

        if (!clientIdFromEnv) {
          message.error('Google Client ID não configurado');
          window.removeEventListener('message', handleOAuthMessage);
          return;
        }

        const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientIdFromEnv}&redirect_uri=${encodeURIComponent(
          redirectUri
        )}&response_type=code&scope=${encodeURIComponent(scopes)}&access_type=offline&prompt=consent`;

        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        window.open(
          authUrl,
          'OAuth',
          `width=${width},height=${height},left=${left},top=${top}`
        );
      }
    } catch (error: any) {
      console.error('Error creating OAuth credential:', error);
      message.error({
        content: error.message || 'Erro ao criar credencial',
        key: 'createOAuth',
      });
    }
  };

  const handleOpenFormSubmission = () => {
    if (formSubmission) {
      setFormSubmissionData(formSubmission);
      setIsFormSubmissionModalOpen(true);
    }
  };

  const renderConfigField = (field: any) => {
    const commonProps = {
      key: field.id,
      label: field.label,
      name: field.id,
      rules: field.required ? [{ required: true, message: `${field.label} é obrigatório` }] : [],
      initialValue: field.current_value || field.default_value,
    };

    switch (field.type) {
      case 'number':
        return (
          <Form.Item {...commonProps}>
            <InputNumber style={{ width: '100%' }} placeholder={field.label} />
          </Form.Item>
        );

      case 'boolean':
        return (
          <Form.Item {...commonProps} valuePropName="checked">
            <Checkbox>{field.label}</Checkbox>
          </Form.Item>
        );

      case 'textarea':
        return (
          <Form.Item {...commonProps}>
            <Input.TextArea rows={4} placeholder={field.label} />
          </Form.Item>
        );

      case 'text':
      default:
        return (
          <Form.Item {...commonProps}>
            <Input placeholder={field.label} />
          </Form.Item>
        );
    }
  };

  const formatCurrency = (value?: number | null) => {
    if (!value) return 'R$ 0,00';
    return Number(value).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  if (loadingProduct) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
        <Paragraph style={{ marginTop: 16 }}>Carregando detalhes do produto...</Paragraph>
      </div>
    );
  }

  if (!clientProduct) {
    console.error('❌ Product not found. URL params:', { productId, clientId });
    return (
      <div>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`/admin/clients/${clientId}/products`)}
          style={{ marginBottom: 16 }}
        >
          Voltar para Produtos do Cliente
        </Button>
        <Alert
          message="Produto não encontrado"
          description={
            <div>
              <p>O produto do cliente não foi encontrado. Verifique se:</p>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                <li>O produto pertence a este cliente</li>
                <li>O ID do produto está correto</li>
              </ul>
              <p style={{ fontSize: '12px', color: '#999', marginTop: 12 }}>
                <strong>Debug info:</strong><br />
                Product ID: {productId}<br />
                Client ID: {clientId}
              </p>
            </div>
          }
          type="error"
          showIcon
          style={{ marginTop: 16 }}
        />
      </div>
    );
  }

  return (
    <div>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(`/admin/clients/${clientId}/products`)}
        style={{ marginBottom: 16 }}
      >
        Voltar para Produtos do Cliente
      </Button>

      <Title level={2}>{clientProduct.product?.name}</Title>
      <Paragraph type="secondary">
        Gerencie todos os detalhes e configurações deste produto para o cliente{' '}
        <strong>{
          clientProduct.client?.full_name
            ? clientProduct.client.full_name
            : 'Cliente sem nome'
        }</strong>
      </Paragraph>

      {/* Client Information Card */}
      <Card 
        title="Informações do Cliente" 
        style={{ marginBottom: 24 }}
        size="small"
      >
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small" bordered>
          <Descriptions.Item label="Nome">
            {clientProduct.client?.full_name || 'Não informado'}
          </Descriptions.Item>
          <Descriptions.Item label="ID">
            <Text copyable style={{ fontSize: 12, fontFamily: 'monospace' }}>
              {clientProduct.client_id}
            </Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          {/* Product Information */}
          <Card
            title={
              <Space>
                <AppstoreOutlined />
                <span>Informações do Produto</span>
              </Space>
            }
            extra={
              <Space>
                <Switch
                  checked={clientProduct.is_active}
                  loading={toggleProductMutation.isPending}
                  checkedChildren="Ativo"
                  unCheckedChildren="Inativo"
                  onChange={handleToggleActive}
                />
                <Tag
                  color={clientProduct.is_active ? 'green' : 'red'}
                  icon={clientProduct.is_active ? <CheckCircleOutlined /> : <StopOutlined />}
                >
                  {clientProduct.is_active ? 'Ativo' : 'Inativo'}
                </Tag>
              </Space>
            }
          >
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Nome do Produto">
                {clientProduct.product?.name}
              </Descriptions.Item>
              <Descriptions.Item label="Descrição">
                {clientProduct.product?.description || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Valor Mensal">
                {formatCurrency(clientProduct.product?.monthly_price)}
              </Descriptions.Item>
              <Descriptions.Item label="Taxa de Adesão">
                {formatCurrency(clientProduct.product?.setup_fee)}
              </Descriptions.Item>
              <Descriptions.Item label="Contratado em">
                {new Date(clientProduct.created_at).toLocaleString('pt-BR')}
              </Descriptions.Item>
              <Descriptions.Item label="Workflow Clonado">
                {clientProduct.cloned_workflow_id ? (
                  <Tag color="success">Sim</Tag>
                ) : (
                  <Tag color="warning">Não</Tag>
                )}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                type="primary"
                icon={<SettingOutlined />}
                onClick={handleOpenConfigModal}
                block
              >
                Configurar Variáveis do Cliente
              </Button>
              <Button
                icon={<SettingOutlined />}
                onClick={handleOpenAdminConfigModal}
                block
              >
                Configurar Variáveis do Admin
              </Button>
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          {/* Form Submission Card */}
          {formSubmission && (
            <Card
              title={
                <Space>
                  <FileTextOutlined />
                  <span>Formulário Preenchido</span>
                </Space>
              }
              style={{ marginBottom: 16 }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Descriptions column={1} bordered size="small">
                  <Descriptions.Item label="Formulário">
                    {formSubmission.form?.name || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Enviado em">
                    {(formSubmission.submitted_at || formSubmission.created_at) 
                      ? new Date(formSubmission.submitted_at || formSubmission.created_at).toLocaleString('pt-BR')
                      : 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    {(() => {
                      const statusMap: Record<string, any> = {
                        pending_submission: { color: 'warning', label: 'Em Análise' },
                        submitted: { color: 'warning', label: 'Em Análise' },
                        approved: { color: 'success', label: 'Aprovado' },
                        rejected: { color: 'error', label: 'Rejeitado' },
                        archived: { color: 'default', label: 'Arquivado' },
                      };
                      const s = formSubmission.status as string;
                      const mapped = statusMap[s] || { color: 'default', label: s };
                      return <Tag color={mapped.color}>{mapped.label}</Tag>;
                    })()}
                  </Descriptions.Item>
                  {formSubmission.form_data && (
                    <Descriptions.Item label="Campos Preenchidos">
                      {Object.keys(formSubmission.form_data).length} campo(s)
                    </Descriptions.Item>
                  )}
                </Descriptions>

                <Button
                  type="primary"
                  icon={<EyeOutlined />}
                  onClick={handleOpenFormSubmission}
                  block
                >
                  Ver Detalhes do Formulário
                </Button>
              </Space>
            </Card>
          )}

          {!formSubmission && !loadingFormSubmission && (
            <Card
              title={
                <Space>
                  <FileTextOutlined />
                  <span>Formulário</span>
                </Space>
              }
              style={{ marginBottom: 16 }}
            >
              <Alert
                message="Nenhum formulário preenchido"
                description="O cliente ainda não preencheu o formulário para este produto."
                type="info"
                showIcon
              />
            </Card>
          )}

          {/* OAuth Credentials */}
          {clientProduct.cloned_workflow_id && (
            <OAuthCredentialsSection
              clientProduct={clientProduct}
              onConnect={handleOpenOAuthModal}
            />
          )}
        </Col>
      </Row>

      {/* Form Submission Details Modal */}
      <Modal
        title={`Detalhes do Formulário - ${formSubmissionData?.form?.name || 'Formulário'}`}
        open={isFormSubmissionModalOpen}
        onCancel={() => {
          setIsFormSubmissionModalOpen(false);
          setFormSubmissionData(null);
        }}
        footer={[
          <Button key="close" onClick={() => setIsFormSubmissionModalOpen(false)}>
            Fechar
          </Button>,
        ]}
        width={700}
      >
        {formSubmissionData && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Cliente">
                {clientProduct.client?.full_name || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Produto">
                {clientProduct.product?.name || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Data de Envio">
                {(formSubmissionData.submitted_at || formSubmissionData.created_at)
                  ? new Date(formSubmissionData.submitted_at || formSubmissionData.created_at).toLocaleString('pt-BR')
                  : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {(() => {
                  const statusMap: Record<string, any> = {
                    pending_submission: { color: 'warning', label: 'Em Análise' },
                    submitted: { color: 'warning', label: 'Em Análise' },
                    approved: { color: 'success', label: 'Aprovado' },
                    rejected: { color: 'error', label: 'Rejeitado' },
                    archived: { color: 'default', label: 'Arquivado' },
                  };
                  const s = formSubmissionData.status as string;
                  const mapped = statusMap[s] || { color: 'default', label: s };
                  return <Tag color={mapped.color}>{mapped.label}</Tag>;
                })()}
              </Descriptions.Item>
            </Descriptions>

            <div>
              <Title level={5}>Dados Preenchidos</Title>
              {formSubmissionData.form_data ? (
                <Card>
                  <Descriptions column={1} bordered size="small">
                    {Object.entries(formSubmissionData.form_data).map(([key, value]) => {
                      // Try to find field metadata from several possible sources
                      const findField = () => {
                        const sources: any[] = [
                          (formSubmissionData.form && formSubmissionData.form.fields) || [],
                          (formSubmissionData.form_template && formSubmissionData.form_template.form && formSubmissionData.form_template.form.fields) || [],
                          (clientProduct?.product && clientProduct.product.form_fields) || [],
                          (formSubmissionData.form_template && formSubmissionData.form_template.form_fields) || [],
                        ];
                        for (const s of sources) {
                          if (!Array.isArray(s)) continue;
                          const found = s.find((f: any) => {
                            if (!f) return false;
                            return (
                              String(f.id) === String(key) ||
                              String(f.n8n_name) === String(key) ||
                              String(f.name) === String(key) ||
                              String(f.field_key) === String(key)
                            );
                          });
                          if (found) return found;
                        }
                        return null;
                      };
                      const field = findField();
                      const label = field?.label || field?.n8n_name || field?.name || key;
                      
                      return (
                        <Descriptions.Item key={key} label={label}>
                          {typeof value === 'object' && value !== null
                            ? JSON.stringify(value)
                            : String(value)}
                        </Descriptions.Item>
                      );
                    })}
                  </Descriptions>
                </Card>
              ) : (
                <Alert
                  message="Nenhum dado disponível"
                  type="info"
                  showIcon
                />
              )}
            </div>
          </Space>
        )}
      </Modal>

      {/* Config Modal - Cliente */}
      <Modal
        title="Configurar Variáveis do Cliente"
        open={isConfigModalOpen}
        onCancel={() => {
          setIsConfigModalOpen(false);
          configForm.resetFields();
        }}
        footer={null}
        width={700}
        closable={!isSavingConfig}
        maskClosable={!isSavingConfig}
        keyboard={!isSavingConfig}
      >
        <Form form={configForm} layout="vertical" onFinish={handleSaveConfig} disabled={isSavingConfig}>
          {loadingConfig ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <Paragraph style={{ marginTop: 16 }}>Carregando configurações...</Paragraph>
            </div>
          ) : !productConfig || !productConfig.fields || productConfig.fields.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              <SettingOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <Paragraph>Este workflow não possui variáveis configuráveis.</Paragraph>
            </div>
          ) : (
            <>
              <Alert
                message="Configuração do Workflow do Cliente"
                description={`Workflow: ${productConfig.workflow_name || 'N/A'}${productConfig?.selected_node_name ? ` • Node: ${productConfig.selected_node_name}` : ''}`}
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />
              {productConfig.fields.map((field: any) => renderConfigField(field))}
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={isSavingConfig}>
                    {isSavingConfig ? 'Salvando...' : 'Salvar Configuração'}
                  </Button>
                  <Button onClick={() => setIsConfigModalOpen(false)} disabled={isSavingConfig}>Cancelar</Button>
                </Space>
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      {/* Config Modal - Admin */}
      <Modal
        title="Configurar Variáveis do Admin"
        open={isAdminConfigModalOpen}
        onCancel={() => {
          setIsAdminConfigModalOpen(false);
          adminConfigForm.resetFields();
        }}
        footer={null}
        width={700}
        closable={!isSavingAdminConfig}
        maskClosable={!isSavingAdminConfig}
        keyboard={!isSavingAdminConfig}
      >
        <Form form={adminConfigForm} layout="vertical" onFinish={handleSaveAdminConfig} disabled={isSavingAdminConfig}>
          {loadingAdminConfig ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <Paragraph style={{ marginTop: 16 }}>Carregando configurações...</Paragraph>
            </div>
          ) : !adminProductConfig ||
            !adminProductConfig.fields ||
            adminProductConfig.fields.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              <SettingOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <Paragraph>Este workflow não possui variáveis admin configuráveis.</Paragraph>
            </div>
          ) : (
            <>
              <Alert
                message="Configuração Admin do Workflow"
                description={`Workflow: ${adminProductConfig.workflow_name || 'N/A'}`}
                type="warning"
                showIcon
                style={{ marginBottom: 24 }}
              />
              {adminProductConfig.fields.map((field: any) => renderConfigField(field))}
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={isSavingAdminConfig}>
                    {isSavingAdminConfig ? 'Salvando...' : 'Salvar Configuração'}
                  </Button>
                  <Button onClick={() => setIsAdminConfigModalOpen(false)} disabled={isSavingAdminConfig}>Cancelar</Button>
                </Space>
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      {/* OAuth Modal */}
      <Modal
        title={`Conectar ${selectedCredential?.type || 'API'}`}
        open={isOAuthModalOpen}
        onOk={handleCreateOAuthCredential}
        onCancel={() => {
          setIsOAuthModalOpen(false);
          setSelectedCredential(null);
          oauthForm.resetFields();
        }}
        okText={createOAuthCredential.isPending ? 'Conectando...' : 'Conectar API'}
        cancelText="Cancelar"
        confirmLoading={createOAuthCredential.isPending}
        closable={!createOAuthCredential.isPending}
        maskClosable={!createOAuthCredential.isPending}
        keyboard={!createOAuthCredential.isPending}
        cancelButtonProps={{ disabled: createOAuthCredential.isPending }}
      >
        <Alert
          message="Conectar API para o Cliente"
          description={`Você está prestes a conectar a API ${
            selectedCredential?.type || ''
          } em nome do cliente. As credenciais serão vinculadas ao produto do cliente.`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={oauthForm} layout="vertical" disabled={createOAuthCredential.isPending}>
          {selectedCredential?.fields?.map((field: any) => (
            <Form.Item
              key={field.name}
              label={field.displayName}
              name={field.name}
              rules={[{ required: field.required, message: `${field.displayName} é obrigatório` }]}
            >
              <Input placeholder={`Digite ${field.displayName}`} />
            </Form.Item>
          ))}
        </Form>
      </Modal>
    </div>
  );
};
